import { queryKeys } from '@query/keys'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { useSessionLanguage } from '@context/SessionSettingsContext'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { LuckyDrawActivityType, LuckyDrawResultItem } from '@model/LuckyDrawDto'

type HistoryData = {
  results: LuckyDrawResultItem[]
  activityTypes: Record<number, string>
}

export const useLuckyDrawHistory = (campaignId: string) => {
  const locale = useSessionLanguage()
  const apiClient = useApiClient(ClientApiClient)

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.luckyDrawHistory(campaignId, locale),
    queryFn: async (): Promise<HistoryData> => {
      const [resultsRes, activityTypesRes] = await Promise.all([
        apiClient.getLuckyDrawResults(campaignId, locale),
        apiClient.getLuckyDrawActivityTypes(locale),
      ])
      const map: Record<number, string> = {}
      activityTypesRes.forEach((t: LuckyDrawActivityType) => (map[t.id] = t.name))
      const results = resultsRes.items ?? []
      return { results, activityTypes: map }
    },
    enabled: !!campaignId,
  })

  return useMemo(() => {
    const results = data?.results ?? []
    const activityTypes = data?.activityTypes ?? {}
    return { results, activityTypes, isLoading }
  }, [data, isLoading])
}
